<template>
  <td
    :class="colorClass"
    style="padding: 0px !important"
  >
    <div style="font-size: 15px !important">
      {{ calc }}
    </div>
    <div
      v-show="is_show_workpattern_val && work_category !== '6' && work_category !== '7'"
      style="font-size: 12px"
    >
      <v-tooltip
        top
        content-class="custom-tooltip"
        v-if="calc_min_max.detail && calc_min_max.detail.length > 0"
      >
        <template v-slot:activator="{ on, attrs }">
          <span
            v-bind="attrs"
            v-on="on"
          >
            ({{ calc_min_max.min }}-{{ calc_min_max.max }})
          </span>
        </template>
        <span
          v-for="(item, index) in calc_min_max.detail"
          :key="index"
        >
          {{ item.workpattern_name }}　{{ item.min }} - {{ item.max }}
          <br />
        </span>
      </v-tooltip>
      <span v-else> ({{ calc_min_max.min }}-{{ calc_min_max.max }}) </span>
    </div>
  </td>
</template>

<style scoped>
.lower {
  background: #ffffff !important;
  color: red !important;
}
.upper {
  background: #ffffff !important;
  color: blue !important;
}
.custom-tooltip {
  background-color: white;
  color: black;
  padding: 10px;
  border-radius: 4px;
  font-size: 12px;
}
</style>

<script>
// 列の種類を定義
const COL = {
  EARLY: '0', // 早番
  DAY: '1', // 日勤
  LATE: '2', // 遅番
  NIGHT: '3', // 夜勤
  DAWN: '4', // 明番
  PUBLIC_HOLIDAY: '5', // 所定休日
  PAID_HOLIDAY: '6', // 有給
  OTHER: '7', // その他
  TOAL_WORK_HOURS: '8', // 総労働時間
};

export default {
  name: 'SummaryColumn',
  props: {
    // edit_mode true:edit false:nomal
    edit_mode: { type: Boolean, default: false },
    shifts: { type: Object },
    work_category: { type: String },
    monthly_working_count: { type: Object },
    count: { type: Number, default: 0 },
    no: { type: Number, default: 0 },
    is_show_workpattern_val: { type: Boolean, require: true },
    office_staff_workpattern: { type: Object },
    office_staffs: { type: Object },
    shiftKey: { type: String },
  },
  methods: {
    /**
     * このコンポーネントのカラムが、指定したカラム名リストに含まれるか
     * @param {Array} colList カラム名リスト
     */
    isColumnIn(colList) {
      return colList.includes(this.work_category);
    },
    /**
     * シフトデータから、指定した勤務区分カテゴリの勤務区分オブジェクトを取得する
     * @param {Object} shift シフトデータ
     * @param {Array} categories 勤務区分カテゴリのリスト
     */
    getWorkpatternFromShiftByCategories(shift, categories) {
      if (categories.includes(shift.workpattern?.category)) {
        return shift.workpattern;
      }
      if (categories.includes(shift.replacement_workpattern?.category)) {
        return shift.replacement_workpattern;
      }
      if (categories.includes(shift.holiday_workpattern?.category)) {
        return shift.holiday_workpattern;
      }
      return null;
    },
    /**
     * シフトデータから、指定した勤務区分タイプの勤務区分オブジェクトを取得する
     * @param {Object} shift シフトデータ
     * @param {Array} types 勤務区分タイプのリスト
     */
    getWorkpatternFromShiftByTypes(shift, types) {
      if (types.includes(shift.workpattern?.type)) {
        return shift.workpattern;
      }
      if (types.includes(shift.replacement_workpattern?.type)) {
        return shift.replacement_workpattern;
      }
      if (types.includes(shift.holiday_workpattern?.type)) {
        return shift.holiday_workpattern;
      }
      return null;
    },
  },
  computed: {
    colorClass() {
      // 早番、日勤、遅番、夜勤、明番、所定休日
      if (this.isColumnIn([COL.EARLY, COL.DAY, COL.LATE, COL.NIGHT, COL.DAWN, COL.PUBLIC_HOLIDAY])) {
        // 最小も最大も0の場合は何もしない
        if (this.calc_min_max.min === 0 && this.calc_min_max.max === 0) {
          return '';
        }
        if (this.calc < this.calc_min_max.min) {
          return 'lower';
        }
        if (this.calc > this.calc_min_max.max) {
          return 'upper';
        }
      }
      // 総労働時間
      if (this.isColumnIn([COL.TOAL_WORK_HOURS])) {
        if (this.office_staffs?.min_worktime_minutes && this.office_staffs?.max_worktime_minutes) {
          const minWorkTimeHours = Number(this.office_staffs.min_worktime_minutes / 60);
          const maxWorkTimeHours = Number(this.office_staffs.max_worktime_minutes / 60);

          // 最小も最大も0の場合は何もしない
          if (minWorkTimeHours === 0 && maxWorkTimeHours === 0) {
            return '';
          }

          if (Number(this.calc) < minWorkTimeHours) {
            return 'lower';
          }

          if (Number(this.calc) > maxWorkTimeHours) {
            return 'upper';
          }
        }
      }
    },
    calc() {
      let num = 0;
      // 日毎のシフトデータをループ
      for (const day of Object.values(this.shifts)) {
        if (!Object.hasOwn(day, this.shiftKey)) {
          continue;
        }
        const shift = day[this.shiftKey];

        // カウント対象のカテゴリーによって処理を分岐
        switch (true) {
          // 勤務(早番、日勤、遅番、夜勤、明番)の場合
          case this.isColumnIn([COL.EARLY]) &&
            this.getWorkpatternFromShiftByCategories(shift, ['early']) !== null:
          case this.isColumnIn([COL.DAY]) &&
            this.getWorkpatternFromShiftByCategories(shift, ['day']) !== null:
          case this.isColumnIn([COL.LATE]) &&
            this.getWorkpatternFromShiftByCategories(shift, ['late']) !== null:
          case this.isColumnIn([COL.NIGHT]) &&
            this.getWorkpatternFromShiftByCategories(shift, ['night']) !== null:
          case this.isColumnIn([COL.DAWN]) &&
            this.getWorkpatternFromShiftByCategories(shift, ['dawn']) !== null:
            // replacement_workpattern がある場合は 0.5 を加算
            if (shift.replacement_workpattern?.category) {
              num += 0.5;
              break;
            }

            // workpatternType に応じて加算
            num += shift.workpattern?.type === 'half_work' ? 0.5 : 1;

            break;
          // 公休の場合
          case this.isColumnIn([COL.PUBLIC_HOLIDAY]):
            if (this.getWorkpatternFromShiftByTypes(shift, ['public_holidays'])) {
              num += 1;
              break;
            }

            if (this.getWorkpatternFromShiftByTypes(shift, ['public_half_holiday'])) {
              num += 0.5;
              break;
            }

            break;
          // 有休の場合
          case this.isColumnIn([COL.PAID_HOLIDAY]):
            if (this.getWorkpatternFromShiftByTypes(shift, ['paid_holidays'])) {
              num += 1;
              break;
            }

            if (this.getWorkpatternFromShiftByTypes(shift, ['paid_half_holiday'])) {
              num += 0.5;
              break;
            }

            break;
          // その他の場合
          case this.isColumnIn([COL.OTHER]):
            if (this.getWorkpatternFromShiftByTypes(shift, ['other_holiday'])) {
              num += 1;
              break;
            }

            break;
          // 総労働時間の場合
          case this.isColumnIn([COL.TOAL_WORK_HOURS]): {
            // 稼働時間を加算（勤務 または 半勤務）
            const workpattern = this.getWorkpatternFromShiftByTypes(shift, ['work', 'half_work']);

            if (workpattern !== null) {
              const parts = shift.workpattern.working_hours.split(':');
              const hours = Number.parseInt(parts[0]);
              const minutes = Number.parseInt(parts[1]);
              const seconds = Number.parseInt(parts[2]);
              const addTime = hours + minutes / 60 + seconds / 3600;
              num += addTime;
            }

            break;
          }
          default:
            break;
        }
      }

      // 総労働時間の場合は小数点2桁（切り捨て）で返却
      if (this.isColumnIn([COL.TOAL_WORK_HOURS])) {
        num = Math.floor(num * 100) / 100;
        return num.toFixed(1);
      }
      return num;
    },
    calc_min_max() {
      let compare_category;
      const result = { min: 0, max: 0, detail: [] };
      switch (this.work_category) {
        case COL.EARLY:
          compare_category = 'early';
          break;
        case COL.DAY:
          compare_category = 'day';
          break;
        case COL.LATE:
          compare_category = 'late';
          break;
        case COL.NIGHT:
          compare_category = 'night';
          break;
        case COL.DAWN:
          compare_category = 'dawn';
          break;
        case COL.PUBLIC_HOLIDAY:
          compare_category = 'holiday';
          break;
        case COL.TOAL_WORK_HOURS:
          if (
            this.office_staffs &&
            this.office_staffs.min_worktime_minutes !== null &&
            this.office_staffs.max_worktime_minutes !== null
          ) {
            result.min = (this.office_staffs.min_worktime_minutes / 60).toFixed(1);
            result.max = (this.office_staffs.max_worktime_minutes / 60).toFixed(1);
          }
          return result;
        default:
          break;
      }

      if (this.office_staff_workpattern !== null && this.office_staff_workpattern !== undefined) {
        Object.values(this.office_staff_workpattern).forEach(
          ({
            min_monthly_working_count,
            max_monthly_working_count,
            category,
            workpattern_name,
            workpattern_type,
          }) => {
            if (category !== compare_category) {
              return;
            }

            // 休日の場合は公休（全休）のみカウント
            if (category !== 'holiday' || workpattern_type === 'public_holidays') {
              result.min += min_monthly_working_count;
              result.max += max_monthly_working_count;
              result.detail.push({
                min: min_monthly_working_count,
                max: max_monthly_working_count,
                workpattern_name,
                workpattern_type,
              });
              return;
            }
          }
        );
      }
      return result;
    },
  },
};
</script>
